import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { Box, Button, Grid, Typography } from "@mui/material";
import { Alert } from "@mui/material";
import { Formik, Form, useFormikContext } from "formik";
import * as Yup from "yup";
import LoadingButton from "../../../../components/Buttons/LoadingButton";
import Modal from "../../../../components/Modal";
import { instructorStatuses, instructorTypes } from "../../../../constants";
import { phoneNumberValidation } from "../../../../utils/validation";
import TextField from "../../../../components/FormikFields/TextField";
import PhoneNumberInput from "../../../../components/FormikFields/PhoneNumberInput";
import SwitchField from "../../../../components/FormikFields/SwitchField";
import StateSelector from "../../../../components/FormikFields/StateSelector";
import { useTranslation } from "react-i18next";
import { useInstructorCandidates } from "../../../../hooks/manageInstructorsLocations";
import ModalTitleDropdown from "../../../../components/Modal/ModalTitleDropdown";
import { find } from "lodash";
import ComboBox from "../../../../components/FormikFields/ComboBox";
import { useTrainingObjects } from "../../../../hooks/apiHooks/trainingObjects";
import InstructorSessions from "../../../../components/InstructorSessions/InstructorSessions";
import LoadingSpinner from "../../../../components/LoadingSpinner/LoadingSpinner";

const EditInstructorModalTitle = ({ isAddingNewInstructor }) => {
  const { t } = useTranslation();
  const { values, setFieldValue, resetForm } = useFormikContext();
  const instructorTypeOptions = Object.values(instructorTypes).map(
    instructorType => ({
      value: instructorType,
      label: t("editInstructorModal.instructorType", {
        context: instructorType
      })
    })
  );

  return isAddingNewInstructor ? (
    <ModalTitleDropdown
      label={`${t("manageCourses.addNewLabel")}:`}
      value={values.instructorType}
      options={instructorTypeOptions}
      onChange={value => {
        resetForm();
        setFieldValue("instructorType", value);
      }}
      selectWidth={270}
    />
  ) : (
    <Typography variant="h6" color="textPrimary">
      {values.instructorType === instructorTypes.external
        ? t("editInstructorModal.editExternalInstructorDetails")
        : t("editInstructorModal.editInternalInstructorDetails")}
    </Typography>
  );
};

const InstructorMenuItem = ({ optionProps, suggestion }) => {
  return (
    <Box component="li" {...optionProps}>
      <Box display="flex" flexDirection="column" alignItems="flex-start">
        <Typography variant="subtitle2">{suggestion.label}</Typography>
        <Typography variant="body2" fontStyle="italic">
          {suggestion.location}
        </Typography>
      </Box>
    </Box>
  );
};

const getSchema = t => {
  const fieldWithLimit = (fieldName, limit) =>
    Yup.string().max(
      limit,
      t("validationMessages.cannotExceedLength", {
        type: t(`editInstructorModal.${fieldName}`),
        maxLength: limit
      })
    );
  const maxLength = 40;

  return Yup.object().shape({
    firstName: fieldWithLimit("firstName", maxLength).required(
      t("validationMessages.fieldRequired")
    ),
    lastName: fieldWithLimit("lastName", maxLength).required(
      t("validationMessages.fieldRequired")
    ),
    city: fieldWithLimit("city", maxLength),
    email: fieldWithLimit("email", 255)
      .nullable(true)
      .email(t("validationMessages.validEmail")),
    state: Yup.mixed().required(t("validationMessages.fieldRequired")),
    phone: phoneNumberValidation(t)
  });
};

const EditInstructorForm = ({
  isExternal,
  instructorCandidates,
  shouldReset,
  isAddingNewInstructor,
  isLoadingOptions,
  isLoadingTrainingObjects,
  trainingObjects,
  userHasInstructorRole
}) => {
  const { resetForm, setFieldValue } = useFormikContext();
  const { t } = useTranslation();

  useEffect(() => {
    resetForm();
  }, [shouldReset]);

  const instructorsList =
    instructorCandidates &&
    instructorCandidates
      .filter(
        instructor =>
          instructor &&
          instructor.location &&
          "firstName" in instructor &&
          "lastName" in instructor &&
          "city" in instructor.location &&
          "state" in instructor.location
      )
      .map(instructor => ({
        label: `${instructor.firstName} ${instructor.lastName}`,
        location: `${instructor.location.city}, ${instructor.location.state}`,
        value: instructor.id
      }));

  const handleSelectUser = (e, selectedUser) => {
    const instructorFields = ["firstName", "lastName", "email", "phone"];
    const instructorLocationFields = ["city", "state"];
    const selectedInstructor = find(instructorCandidates, {
      id: selectedUser?.value
    });
    setFieldValue("userId", selectedUser?.value ?? null);
    instructorFields.forEach(field => {
      setFieldValue(
        field,
        selectedInstructor ? selectedInstructor[field] || "" : ""
      );
    });
    instructorLocationFields.forEach(field => {
      setFieldValue(
        field,
        selectedInstructor ? selectedInstructor.location[field] : ""
      );
    });
  };

  return (
    <Form>
      <Grid container spacing={4} sx={{ mt: -0.5, mb: 4 }}>
        {!isExternal && isAddingNewInstructor && (
          <Grid item xs={12}>
            <ComboBox
              defaultValue=""
              autocompleteProps={{
                renderOption: (props, suggestion) => {
                  return (
                    <InstructorMenuItem
                      optionProps={props}
                      suggestion={suggestion}
                    />
                  );
                },
                onChange: handleSelectUser
              }}
              options={instructorsList}
              name="userId"
              placeholder={t("editInstructorModal.instructorLabel")}
              InputProps={{
                inputProps: {
                  "data-qa": "edit-instructor-input"
                }
              }}
              isLoadingOptions={isLoadingOptions}
              disabled={isLoadingOptions}
            />
          </Grid>
        )}
        <Grid item xs={12} sm={6}>
          <TextField
            label={t("editInstructorModal.firstName")}
            name={"firstName"}
            inputProps={{
              "data-qa": "edit-instructor-first-name-field"
            }}
            tooltip={
              !isExternal && t("editInstructorModal.internalInstructorTooltip")
            }
            disabled={!isExternal}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label={t("editInstructorModal.lastName")}
            name="lastName"
            inputProps={{
              "data-qa": "edit-instructor-last-name-field"
            }}
            tooltip={
              !isExternal && t("editInstructorModal.internalInstructorTooltip")
            }
            disabled={!isExternal}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            disabled={!isExternal}
            label={t("editInstructorModal.email")}
            name="email"
            tooltip={
              !isExternal && t("editInstructorModal.internalInstructorTooltip")
            }
            inputProps={{
              "data-qa": "edit-instructor-email-field"
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            disabled={!isExternal}
            label={t("editInstructorModal.city")}
            name="city"
            tooltip={
              !isExternal && t("editInstructorModal.internalInstructorTooltip")
            }
            inputProps={{
              "data-qa": "edit-instructor-city-field"
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StateSelector
            disabled={!isExternal}
            disableUnderline={false}
            tooltip={
              !isExternal
                ? t("editInstructorModal.internalInstructorTooltip")
                : ""
            }
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <PhoneNumberInput
            name="phone"
            label={t("editInstructorModal.phone")}
            inputProps={{
              "data-qa": "edit-instructor-phone-field"
            }}
          />
        </Grid>
        {!isAddingNewInstructor && (
          <Grid item>
            {isLoadingTrainingObjects ? (
              <LoadingSpinner />
            ) : (
              <Box
                height="100%"
                display="flex"
                alignItems="center"
                maxWidth={260}
              >
                <SwitchField
                  name="statusType.id"
                  checkedValue={instructorStatuses.active}
                  uncheckedValue={instructorStatuses.inactive}
                  onLabel={t("editInstructorModal.statusActive")}
                  offLabel={t("editInstructorModal.statusInactive")}
                  label={t("editInstructorModal.status")}
                  tooltip={
                    !isExternal &&
                    userHasInstructorRole &&
                    t("editInstructorModal.internalInstructorStatusTooltip")
                  }
                  disabled={
                    (!isExternal && userHasInstructorRole) ||
                    trainingObjects?.length > 0
                  }
                />
              </Box>
            )}
            {!isLoadingTrainingObjects && trainingObjects?.length > 0 && (
              <InstructorSessions sessions={trainingObjects} />
            )}
          </Grid>
        )}
        {!isExternal && (
          <>
            <Grid item xs={12} sx={{ mt: 1 }}>
              <Typography variant="body2" color="textSecondary">
                {`*${t("editInstructorModal.editInternalMsg")}`}.
              </Typography>
            </Grid>
            <Box mt={3}>
              <Alert severity="info">
                {t("editInstructorModal.manageIltsNote")}
              </Alert>
            </Box>
          </>
        )}
      </Grid>
    </Form>
  );
};

const EditInstructorModal = ({
  existingInstructor,
  onClose,
  open,
  onSave,
  defaultInstructorType
}) => {
  const { t } = useTranslation();

  const [trainingObjects, setTrainingObjects] = useState(null);

  const {
    fetchInstructorCandidates,
    instructorCandidates,
    isLoading
  } = useInstructorCandidates();

  const {
    getTrainingObjects,
    isLoading: isLoadingTrainingObjects
  } = useTrainingObjects({
    fields: {
      trainingObject: ["id", "name", "startDate", "endDate"]
    }
  });

  useEffect(() => {
    if (
      existingInstructor &&
      existingInstructor.statusType?.id == instructorStatuses.active
    ) {
      getTrainingObjects({
        filters: {
          instructor: existingInstructor.id
        }
      }).then(response => {
        setTrainingObjects(response.data);
      });
    } else {
      setTrainingObjects(null);
    }
  }, [existingInstructor]);

  return (
    <>
      <Formik
        initialValues={{
          firstName: existingInstructor?.firstName ?? "",
          lastName: existingInstructor?.lastName ?? "",
          email: existingInstructor?.email ?? "",
          phone: existingInstructor?.phone ?? "",
          city: existingInstructor?.city ?? "",
          state: existingInstructor?.state ?? "",
          statusType: {
            id: existingInstructor?.statusType?.id ?? instructorStatuses.active,
            type: "statusType"
          },
          instructorType: defaultInstructorType,
          userId: null,
          userHasInstructorRole: existingInstructor?.userHasInstructorRole ?? ""
        }}
        validationSchema={getSchema(t)}
        enableReinitialize
        onSubmit={newInstructorData =>
          onSave(newInstructorData).then(() => fetchInstructorCandidates())
        }
      >
        {({ submitForm, isSubmitting, values }) => (
          <>
            <Modal
              scrollable
              maxWidth="md"
              dialogTitle={
                <EditInstructorModalTitle
                  isAddingNewInstructor={!existingInstructor}
                />
              }
              dialogContent={
                <EditInstructorForm
                  isLoadingOptions={isLoading}
                  isLoadingTrainingObjects={isLoadingTrainingObjects}
                  trainingObjects={trainingObjects}
                  isExternal={
                    values.instructorType === instructorTypes.external
                  }
                  userHasInstructorRole={values.userHasInstructorRole}
                  shouldReset={open}
                  instructorCandidates={instructorCandidates}
                  isAddingNewInstructor={!existingInstructor}
                />
              }
              dialogActions={
                <>
                  <Button color="primary" onClick={onClose}>
                    {t("cancelBtn")}
                  </Button>
                  <LoadingButton
                    pending={isSubmitting}
                    color="primary"
                    variant="contained"
                    onClick={submitForm}
                    disabled={isSubmitting}
                    data-qa="edit-instructor-save-btn"
                  >
                    {t("saveBtn")}
                  </LoadingButton>
                </>
              }
              open={open}
              onClose={onClose}
              showCloseIcon
              data-qa="edit-instructor-modal"
            />
          </>
        )}
      </Formik>
    </>
  );
};

EditInstructorModal.propTypes = {
  /**
   * Called to close the modal
   */
  onClose: PropTypes.func,
  /**
   * True if the modal is open, false otherwise
   */
  open: PropTypes.bool,
  /**
   * Called when the form is submitted to save the changes to the instructor
   */
  onSave: PropTypes.func,
  instructor: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    statusType: PropTypes.shape({
      id: PropTypes.string
    }),
    isExternal: PropTypes.bool
  }),
  defaultInstructorType: PropTypes.oneOf(Object.values(instructorTypes))
};

export default EditInstructorModal;
